/**
 * 🦉 WISE OWL TEMPERAMENT
 * Contemplation builds to PROPHECY REVEALED!
 * Ancient wisdom flows through...
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const WiseOwl: Temperament = {
  id: 'wise-owl',
  name: 'Wise Owl',
  emoji: '🦉',
  description: 'Deep meditation builds to PROPHECY! Ancient wisdom shall be revealed!',

  // ============================================================
  // MOODS - Building toward PROPHECY
  // ============================================================

  moods: ['meditating', 'contemplating', 'knowing', 'channeling', 'prophecy', 'drained'],
  initialMood: 'meditating',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    meditating: [
      { mood: 'meditating', weight: 0.55 },
      { mood: 'contemplating', weight: 0.45 },
    ],
    contemplating: [
      { mood: 'meditating', weight: 0.15 },
      { mood: 'contemplating', weight: 0.35 },
      { mood: 'knowing', weight: 0.5 },
    ],
    knowing: [
      { mood: 'contemplating', weight: 0.15 },
      { mood: 'knowing', weight: 0.35 },
      { mood: 'channeling', weight: 0.5 },
    ],
    channeling: [
      { mood: 'knowing', weight: 0.1 },
      { mood: 'channeling', weight: 0.4 },
      { mood: 'prophecy', weight: 0.5 },
    ],
    prophecy: [
      { mood: 'prophecy', weight: 0.55 },
      { mood: 'drained', weight: 0.45 },
    ],
    drained: [
      { mood: 'drained', weight: 0.3 },
      { mood: 'meditating', weight: 0.6 },
      { mood: 'contemplating', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    meditating:    { base: 3500, variance: 4000 },
    contemplating: { base: 600, variance: 800 },
    knowing:       { base: 500, variance: 700 },
    channeling:    { base: 400, variance: 500 },
    prophecy:      { base: 250, variance: 350 },
    drained:       { base: 1500, variance: 2000 },
  },

  moodConstraints: {
    prophecy: {
      minDuration: 3000,
      maxDuration: 6000,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds wisdom!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 5,

    moodDrain: {
      meditating:    -2,   // Slowly gathering wisdom...
      contemplating: -2,   // Deeper thoughts...
      knowing:       -2,   // Understanding grows...
      channeling:    -1,   // Nearly there...
      prophecy:      22,   // Prophecy drains the spirit
      drained:       -5,   // Recovering wisdom
    },

    thresholds: {
      exhausted: 12,
      low: 28,
      high: 55,
      explosive: 75,
    },

    restingMoods: ['meditating', 'drained', 'contemplating', 'knowing'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    meditating: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 600,
      jitter: 0.3,
      glowIntensity: 0.15,
      glowColor: 'rgba(180, 150, 255, 0.3)',
    },
    contemplating: {
      rotation: { x: -18, y: -25, z: 0 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1,
      transitionMs: 350,
      jitter: 1,
      glowIntensity: 0.25,
      glowColor: 'rgba(180, 150, 255, 0.4)',
    },
    knowing: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 250,
      jitter: 2,
      glowIntensity: 0.4,
      glowColor: 'rgba(200, 170, 255, 0.55)',
    },
    channeling: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.02,
      transitionMs: 150,
      jitter: 4,
      glowIntensity: 0.65,
      glowColor: 'rgba(220, 180, 255, 0.7)',
    },
    prophecy: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -3, z: 0 },
      scale: 1.05,
      transitionMs: 80,
      jitter: 6,
      glowIntensity: 1,
      glowColor: 'rgba(255, 215, 0, 1)',
      cssClass: 'prophecy',
    },
    drained: {
      rotation: { x: -22, y: -25, z: 2 },
      translation: { x: 0, y: 3, z: 0 },
      scale: 0.97,
      transitionMs: 600,
      jitter: 0.5,
      glowIntensity: 0.1,
      glowColor: 'rgba(150, 130, 200, 0.2)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for prophecy mode - mystical floating!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -1, y: 1, z: 1 },
      translation: { x: -3, y: -4, z: 0 },
      bulge: { x: 30, y: 30 },
    },
    topRight: {
      transform: { x: -1, y: -1, z: -1 },
      translation: { x: 3, y: -4, z: 0 },
      bulge: { x: 70, y: 30 },
    },
    bottomLeft: {
      transform: { x: 1, y: 1, z: -1 },
      translation: { x: -3, y: 2, z: 0 },
      bulge: { x: 30, y: 70 },
    },
    bottomRight: {
      transform: { x: 1, y: -1, z: 1 },
      translation: { x: 3, y: 2, z: 0 },
      bulge: { x: 70, y: 70 },
    },
  },

  bouncingMoods: ['prophecy', 'channeling'],

  bounceIntervals: {
    prophecy: 200,
    channeling: 350,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'meditating' && energy < 18) {
        return { energyDelta: 4 };
      }
      if (currentMood === 'meditating' && energy >= 18) {
        return { transitionTo: 'contemplating', energyDelta: 3 };
      }
      if (currentMood === 'contemplating') {
        return { transitionTo: 'knowing', energyDelta: 2 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Seeking knowledge awakens wisdom!
      if (currentMood === 'meditating') {
        return { transitionTo: 'contemplating', energyDelta: 18 * intensity };
      }
      if (currentMood === 'contemplating') {
        return { transitionTo: 'knowing', energyDelta: 18 * intensity };
      }
      if (currentMood === 'knowing') {
        return { transitionTo: 'channeling', energyDelta: 20 * intensity };
      }
      if (currentMood === 'channeling') {
        return { transitionTo: 'prophecy', energyDelta: 25 * intensity, forceDuration: 3000 };
      }
      if (currentMood === 'drained') {
        return { transitionTo: 'contemplating', energyDelta: 12 * intensity };
      }
      return { energyDelta: 12 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Urgent seeking = faster wisdom!
      if (currentMood === 'meditating' || currentMood === 'contemplating') {
        return { transitionTo: 'channeling', energyDelta: 32 * intensity };
      }
      if (currentMood === 'knowing' || currentMood === 'channeling' || energy > 45) {
        return { transitionTo: 'prophecy', energyDelta: 38 * intensity, forceDuration: 3500 };
      }
      return { transitionTo: 'channeling', energyDelta: 28 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    meditating: {
      emoji: '🧘',
      text: 'om',
      label: '*deep meditation*',
    },
    contemplating: {
      emoji: '🤔',
      text: '...',
      label: '*pondering existence*',
    },
    knowing: {
      emoji: '😌',
      text: 'hmm',
      label: '*understanding grows*',
    },
    channeling: {
      emoji: '✨',
      text: '?!',
      label: '*ancient symbols appear*',
    },
    prophecy: {
      emoji: '🦉',
      text: '!!!',
      label: '🔮 PROPHECY REVEALED 🔮',
    },
    drained: {
      emoji: '😔',
      text: '~~~',
      label: '*wisdom exhausted*',
    },
  },
};
